import groupBy from 'object.groupby'
import { create } from 'zustand'

import { computed } from '@/stores/computed'
import { $data } from '@/stores/Data'
import { getBillableQuantity, getPreselectedQuantity, ITEMS_PRESELECTED } from '@/util/Hardcoded'

export type Item = {
  partId: string
  name: string
  rNumber: string
  categoryId: string
  hubId: string
  amount: number
  price: number
  preselect: boolean
  currency: string
  limit: number
}

type HubId = string

type CartStore = {
  items: Item[]

  revalidate: () => void
  initialize: () => void
  totalPerHub: () => Record<HubId, number>
  getByRNumber: (rNumber: string) => Item | undefined
  getByRNumberAndHub: (hubId: string, rNumber: string) => Item | undefined
  increase: (item: Item) => void
  decrease: (item: Item) => void
  reset: () => void
}

const generateCartFromJSON = (): Item[] => {
  const data = $data.get()
  if (!data) return []
  const items: Item[] = []

  for (const hub of data.hubs) {
    for (const category of hub.categories) {
      for (const part of category.parts) {
        const isPreselected = ITEMS_PRESELECTED.includes(part.id)

        items.push({
          amount: getPreselectedQuantity(part.id),
          categoryId: category.id,
          name: part.partname,
          hubId: hub.id,
          partId: part.id,
          preselect: isPreselected,
          rNumber: part.RNumber,
          price: Number.parseFloat(part.pricing.retailprice ?? '0'),
          currency: part.pricing.currency,
          limit: part.part_limit,
        })
      }
    }
  }

  return items
}

const calculateTotalPrice = ([hubId, items]: [string, Item[]]) => [
  hubId,
  items.reduce((accumulator, item) => {
    const billableAmount = getBillableQuantity(item.partId, item.amount)
    return accumulator + item.price * billableAmount
  }, 0),
] as [string, number]

const sumBaseHubPrice = ([hubId, priceWithoutHub]: [string, number]) => {
  const data = $data.get()!

  return [
    hubId,
    priceWithoutHub + Number.parseFloat(data.hubs.find(h => h.id === hubId)?.pricing?.retailprice || '0'),
  ]
}

const useCartStore = create<CartStore>()((set, get) => ({
  items: [],
  totalPerHub: computed(
    () => [get().items, $data.get()],
    (items, _) => Object.fromEntries(
      Object.entries(
        groupBy(items, item => item.hubId),
      ).map(calculateTotalPrice)
        .map(sumBaseHubPrice)
      ,
    ) as Record<string, number>,
  ),

  initialize: () => get().reset(),
  reset: () => set({
    items: generateCartFromJSON(),
  }),
  getByRNumber: rNumber => get().items.find(item => item.rNumber === rNumber),
  getByRNumberAndHub: (hubId, rNumber) => get().items.find(item => item.hubId === hubId && item.rNumber === rNumber),

  revalidate: () => {
    const items = get().items
    const data = $data.get()!

    for (const item of items) {
      const part = data.hubs.find(h => h.id === item.hubId)!.categories.flatMap(c => c.parts).find(p => p.id === item.partId)
      if (!part) continue
      item.name = part.partname
      item.rNumber = part.RNumber
      item.price = Number.parseFloat(part.pricing.retailprice ?? '0')
      item.currency = part.pricing.currency
    }
    return set({ items })
  },

  increase: newItem => set((state) => {
    const itemIndex = state.items.findIndex(item => item.partId === newItem.partId)
    const itemExists = itemIndex !== -1
    const amount = (state.items[itemIndex]?.amount ?? 0) + 1
    const item = {
      ...newItem,
      amount,
    }

    // ? Create
    if (itemExists) {
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    return {
      ...state,
      items: [...state.items, item],
    }
  }),

  decrease: newItem => set((state) => {
    const itemIndex = state.items.findIndex(item => item.partId === newItem.partId)
    const itemExists = itemIndex !== -1
    const amount = (state.items.at(itemIndex)?.amount ?? 0) - 1
    const item = {
      ...newItem,
      amount,
    }

    // ? Remove
    if (amount < 1) {
      // return {
      //   ...state,
      //   items: state.items.filter(item => item.partId !== newItem.partId),
      // }
      item.amount = 0
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    if (itemExists) {
      state.items[itemIndex] = item
      return { ...state, items: [...state.items] }
    }

    return {
      ...state,
      items: [...state.items, item],
    }
  }),
}
))

export default useCartStore
